<script setup>
    import { ref, onMounted, onUnmounted, computed } from 'vue'

    import ResponsiveNavLink from '@/Components/ResponsiveNavLink.vue'
    import { Link } from '@inertiajs/vue3'
    import ThemeToggle from '@/Components/ThemeToggle.vue'
    import FooterLinks from '@/Components/navigation/FooterLinks.vue'
    import { usePage } from '@inertiajs/vue3'
    import useSWRV from 'swrv'

    const pathName = window.location.pathname
    const WALLET_BALANCE_KEY = 'walletBalance'

    const user = usePage().props.auth.user
    const { props } = usePage()
    const liveShowsEnabled = ref(import.meta.env.VITE_LIVE_SHOWS_ENABLED)

    defineProps({
        canLogin: {
            type: Boolean,
        },
        canRegister: {
            type: Boolean,
        },

        profileInitials: {
            type: String,
            default: 'OD',
        },
        isCreator: {
            type: Boolean,
            default: false,
        },
        verificationStatus: {
            type: Number,
            default: 0,
        },
    })
    let fullName = ''
    if (props.auth && props.auth.user && props.auth.user.name) {
        fullName = props.auth.user.name
    }
    let initials = ''

    if (fullName) {
        const names = fullName.split(' ')
        initials = names.map(name => name.charAt(0)).join('')
        if (names.length === 1 && initials.length > 2) {
            initials = initials.substring(0, 2)
        }
        initials = initials.toLowerCase()
    }

    const profileInitials = ref(initials || props.profileInitials)

    // Computed property to check if the user is logged in
    const isLoggedIn = ref(props.auth && props.auth.user && props.auth.user.name !== null)
    const fetcher = url => axios.get(url).then(res => res.data)
    const { data: profile } = useSWRV(isLoggedIn.value ? '/profile/resource' : null, fetcher)

    const menuHandleLogout = async () => {
        // Perform logout action here (e.g., making an API request, clearing local storage, etc.)

        // Update isLoggedIn ref after successful logout
        isLoggedIn.value = false

        // Redirect to the desired route after logout (e.g., home page)
        // Replace `route('home')` with the appropriate route path
        await Link.replace(route('home'))
    }

    const CONVERSATION_KEY = 'conversationsCount'
    const NOTIFICATION_KEY = 'notificationsCount'

    const menuItems = [
        { icon: 'film-simple', route: 'creator.manageVideos', label: 'Manage Videos' },
        {
            icon: 'circle-bolt',
            route: 'profile.goLive',
            label: 'Live Girls',
            live: true,
        },
        { icon: 'films', route: 'creator.vault', label: 'DM Vault' },
        { icon: 'list', route: 'profile.message-lists', label: 'Lists' },
        { icon: 'users', route: 'creator.overview', label: 'Fans and Followers' },
        {
            icon: 'comment',
            route: 'chatify',
            label: 'Messages',
            count: '8',
            keyCount: CONVERSATION_KEY,
            params: {
                origin: pathName,
            },
            redirect: true,
        },
        {
            icon: 'bell',
            route: 'notifications',
            label: 'Notifications',
            count: '6',
            keyCount: NOTIFICATION_KEY,
        },
        { icon: 'tag', route: 'creator.paymentPreferences', label: 'Manage Offers' },
        { icon: 'coins', route: 'profile.payouts', label: 'Payouts' },
        { icon: 'chart-column', route: 'profile.dashboard', label: 'Earnings and Stats' },
        { icon: 'gear', route: 'profile.settings', label: 'Settings' },
    ]
    const moreMenuItems = [
        { icon: 'stars', route: 'creator.index', label: 'Creators' },
        {
            icon: 'circle-bolt',
            route: 'https://exncams.com/',
            label: 'Live Girls',
            external: true,
        },
        { icon: 'magnifying-glass', route: 'explore.index', label: 'Explore' },
        {
            icon: 'coins',
            route: 'profile.wallet',
            label: 'Wallet',
            coins: props.auth.user.balance,
            keyCount: WALLET_BALANCE_KEY,
        },
        { icon: 'cart-shopping', route: 'profile.paymentHistory', label: 'Purchases' },
        { icon: 'bookmark', route: 'profile.collections', label: 'Saved' },
        {
            icon: 'heart',
            route: 'profile.show.likes',
            profile: props.auth.user.profile_url,
            view: 'view',
            label: 'Likes',
        },
        { icon: 'user-check', route: 'profile.fansAndFollowings', label: 'Following' },
    ]
    const isMoreMenuOpen = ref(false)
    const menuDiv = ref(null)
    const insideMenu = ref(null)
    const handleClick = () => {
        // Toggle the menu state
        isMoreMenuOpen.value = !isMoreMenuOpen.value

        // If the menu is being opened, then scroll to the top
        if (isMoreMenuOpen.value) {
            menuDiv.value.scrollTop = 0
            insideMenu.value.scrollTop = 0
        }
    }
    const menuContain = ref()
    const resetScrollPosition = () => {
        setTimeout(() => {
            menuDiv.value.scrollTop = 0
            insideMenu.value.scrollTop = 0
        }, 300)
    }
    const getCountValue = (keyCount, profileData) => {
        if (keyCount == CONVERSATION_KEY) {
            return profileData?.conversations_count
        } else if (keyCount == NOTIFICATION_KEY) {
            return profileData?.notifications_count
        }
        return 0
    }
    const getCointsValue = (keyCount, profileData) => {
        if (keyCount == WALLET_BALANCE_KEY) {
            return profileData?.balance
        }
        return 0
    }
    const fathomClickLiveGirls = () => {
        fathom.trackEvent('NavMenuCreator-click live-girls')
    }

    const emit = defineEmits(['openNoLiveModal'])
    const handleLiveClick = e => {
        if (!props?.creator?.allow_live || false) {
            e.preventDefault()
            emit('openNoLiveModal')
        }
    }
</script>
<template>
    <!-- drawer component -->
    <div
        id="right-navigation"
        class="fixed right-0 top-[-56px] z-[1001] block h-screen min-h-screen w-full translate-x-full overflow-hidden bg-transparent transition-transform dark:bg-transparent sm:top-[-85px]"
        tabindex="-1"
        aria-labelledby="drawer-right-label">
        <div
            class="close-menu-area absolute h-[200%] w-[21%] sm:w-[70%]"
            data-drawer-hide="right-navigation"
            @click="isMoreMenuOpen = false"></div>

        <div
            ref="menuDiv"
            class="menuDiv absolute right-0 flex h-[100vh] min-h-screen w-[80%] flex-col bg-black sm:w-[476px]"
            :class="{ hidden: isMoreMenuOpen }">
            <div
                ref="insideMenu"
                id="insideMenu"
                class="insideMenu absolute right-0 top-0 z-[100] flex min-h-screen w-[100%] flex-col overflow-hidden bg-white px-[18px] pb-[100px] pt-[62px] dark:bg-black sm:w-[476px] sm:px-[81px] sm:pt-[40px]">
                <button
                    type="button"
                    @click="isMoreMenuOpen = false"
                    data-drawer-hide="right-navigation"
                    aria-controls="right-navigation"
                    class="absolute right-[15px] top-[20px] z-[1000] inline-flex h-[25px] w-[25px] items-center justify-center bg-transparent text-gray-400 hover:text-gray-900 focus:outline-none dark:hover:text-gray-900 lg:right-[83px] lg:top-[25px] xl:right-[57px] xl:top-[23px]">
                    <font-awesome-icon
                        class="h-[25px] w-[25px] text-iconWhite hover:text-iconhover sm:h-[35px] sm:w-[35px]"
                        :icon="['fas', 'xmark']" />
                </button>
                <!-- Logged In-->
                <div
                    class="mx-auto hidden h-[45px] w-full bg-announcementgray px-5 text-center text-[16px] font-medium leading-[45px] text-white sm:mt-[37px] sm:w-full">
                    You are in the top 2%
                </div>
                <div class="relative mx-auto w-full">
                    <div class="relative flex items-center justify-center">
                        <!-- Flex container -->
                        <div class="relative h-[105px] w-auto">
                            <Link
                                :href="
                                    route('profile.show', [$page.props.auth.user.profile_url], {
                                        backref: pathName,
                                    })
                                "
                                data-drawer-hide="right-navigation"
                                class="relative cursor-pointer">
                                <img
                                    v-if="props.avatarImage"
                                    :src="props.avatarImage"
                                    class="transition-filter duration-400 my-[20px] mr-[15px] h-[75px] w-[75px] cursor-pointer rounded-full transition-transform hover:scale-125 sm:h-[80px] sm:w-[80px]" />
                                <div v-else>
                                    <font-awesome-icon
                                        :icon="['fas', 'circle-user']"
                                        class="mx-auto my-[20px] h-[75px] w-[75px] cursor-pointer rounded-full bg-gray-300 text-neutral-700 hover:text-mintgreen sm:h-[80px] sm:w-[80px]" />
                                </div>
                            </Link>
                            <Link
                                data-drawer-hide="right-navigation"
                                class="absolute bottom-[0px] right-[10px] mx-auto block h-[20px] w-[20px] rounded-lg text-center dark:text-white"
                                :href="route('profile.edit', { backref: pathName })">
                                <font-awesome-icon
                                    class="h-[20px] w-[20px] hover:text-mintgreen"
                                    :icon="['far', 'circle-camera']" />
                            </Link>
                        </div>
                        <Link
                            :href="
                                route('profile.show', [$page.props.auth.user.profile_url], {
                                    backref: pathName,
                                })
                            "
                            data-drawer-hide="right-navigation"
                            class="ml-2 w-full max-w-[150px] overflow-hidden text-ellipsis text-[16px] font-normal tracking-[0.6px] dark:text-white dark:hover:text-mintgreen sm:text-[18px]">
                            {{ $page.props.auth.user.profile_url }}
                        </Link>
                    </div>
                </div>
                <div class="mb-[56px]">
                    <Link
                        :href="route('creator.start', { backref: pathName })"
                        data-drawer-hide="right-navigation"
                        class="mx-auto mt-[25px] flex w-[75%] items-center justify-center rounded-[6px] border border-mintgreen text-center text-[14px] font-medium leading-[52px] dark:text-white dark:hover:text-mintgreen">
                        <font-awesome-icon
                            class="h-[20px] w-[20px] pr-[10px]"
                            :icon="['fas', 'circle-arrow-up']" />
                        Upload
                    </Link>

                    <!-- Create Girlfriend button -->
                    <Link
                        :href="`/create`"
                        data-drawer-hide="right-navigation"
                        class="mx-auto mt-5 flex w-[300px] items-center justify-center space-x-2 rounded-full bg-[#E86599] px-8 py-3 text-white transition-colors duration-200 hover:bg-pink-600">
                        <img src="/images/waveform-icon.png" alt="Waveform" class="max-w-[20px]" />
                        <span class="whitespace-nowrap text-base font-medium"
                            >Create your own Girlfriend</span
                        >
                    </Link>
                </div>

                <div ref="menuContain" class="mb-[40px] flex flex-col space-y-[30px]">
                    <a
                        v-if="props.creator.isCreator && liveShowsEnabled === 'true'"
                        @click="handleLiveClick"
                        :href="
                            route('creator.live_show', props.creator.profile_url, {
                                backref: pathName,
                            })
                        "
                        class="menu-link relative block w-full cursor-pointer border-l-4 border-transparent pr-4 text-left text-[18px] font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen">
                        <font-awesome-icon
                            :icon="['far', 'video']"
                            class="mx-auto mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                        Live Stream
                    </a>
                    <template v-for="item in menuItems" :key="item.label">
                        <a
                            v-if="item.route === 'profile.goLive' && item.live && !item.redirect"
                            v-show="!isCreator"
                            @click="fathomClickLiveGirls()"
                            href="https://exncams.com/"
                            class="menu-link relative flex w-full items-center border-l-4 border-transparent pr-4 text-left text-[16px] text-base font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen sm:text-[18px]"
                            target="_blank"
                            rel="noopener noreferrer">
                            <font-awesome-icon
                                :icon="['fas', item.icon]"
                                class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                            {{ item.label }}
                        </a>

                        <ResponsiveNavLink
                            v-else-if="!item.redirect && !item.profile"
                            :href="route(item.route, { backref: pathName })"
                            @click="resetScrollPosition"
                            class="relative flex items-center text-[16px] sm:text-[18px]"
                            data-drawer-hide="right-navigation">
                            <font-awesome-icon
                                :icon="['fas', item.icon]"
                                class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                            {{ item.label }}
                            <div
                                v-if="getCountValue(item.keyCount, profile) > 0"
                                class="absolute right-0 top-[0px] leading-[40px]">
                                <span
                                    class="block h-[21px] w-[21px] rounded-full bg-alertred text-center text-[10px] font-medium leading-[21px] text-white">
                                    {{ getCountValue(item.keyCount, profile) }}
                                </span>
                            </div>
                        </ResponsiveNavLink>
                        <a
                            v-if="item.redirect"
                            :href="route(item.route, item.params)"
                            class="menu-link relative flex w-full items-center border-l-4 border-transparent pr-4 text-left text-[16px] text-base font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen sm:text-[18px]">
                            <font-awesome-icon
                                :icon="['fas', item.icon]"
                                class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                            {{ item.label }}
                            <div
                                v-if="getCountValue(item.keyCount, profile) > 0"
                                class="absolute right-0 top-[0px] leading-[40px]">
                                <span
                                    class="block h-[21px] w-[21px] rounded-full bg-alertred text-center text-[10px] font-medium leading-[21px] text-white">
                                    {{ getCountValue(item.keyCount, profile) }}
                                </span>
                            </div>
                        </a>
                    </template>
                    <div
                        class="menu-link relative mb-[40px] block w-full cursor-pointer border-l-4 border-transparent pr-4 text-left text-[18px] font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen"
                        @click="handleClick">
                        <font-awesome-icon
                            :icon="['fas', 'circle-ellipsis']"
                            class="mx-auto mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                        More
                    </div>
                </div>

                <div class="dark:text-white">
                    <theme-toggle />
                </div>
                <ResponsiveNavLink
                    class="py-[20px] text-[16px] sm:text-[16px]"
                    data-drawer-hide="right-navigation"
                    :href="route('logout')"
                    method="post"
                    as="button"
                    @click="$emit('logout')">
                    Log Out
                </ResponsiveNavLink>
                <FooterLinks />
            </div>
        </div>
        <div
            id="more-menu"
            class="absolute right-0 top-[0px] z-[1] h-[2200px] w-[80%] bg-white px-[18px] pt-[50px] dark:bg-black sm:w-[476px] sm:px-[81px]"
            :class="{ hidden: !isMoreMenuOpen }">
            <div @click="isMoreMenuOpen = false" class="cursor-pointer text-black dark:text-white">
                <font-awesome-icon class="h-[25px] w-[25px]" :icon="['far', 'arrow-left']" />
            </div>
            <div class="pt-[30px]">
                <div v-for="item in moreMenuItems" :key="item.label">
                    <ResponsiveNavLink
                        v-if="item.route === 'profile.collections'"
                        :href="route(item.route, { tab: 'saved-videos', backref: pathName })"
                        class="menu-link relative flex w-full items-center border-l-4 border-transparent py-[20px] pr-4 text-left text-[16px] text-base font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen sm:text-[18px]"
                        target="_blank"
                        data-drawer-hide="right-navigation"
                        rel="noopener noreferrer">
                        <font-awesome-icon
                            :icon="['fas', item.icon]"
                            class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                        {{ item.label }}
                    </ResponsiveNavLink>

                    <div v-else-if="item.external">
                        <a
                            :href="item.route"
                            class="menu-link relative block w-full border-l-4 border-transparent py-[20px] pr-4 text-left text-[16px] text-base font-medium text-gray-600 transition duration-150 ease-in-out focus:outline-none dark:text-white dark:hover:text-mintgreen sm:text-[18px]"
                            target="_blank"
                            rel="noopener noreferrer">
                            <font-awesome-icon
                                :icon="['fas', item.icon]"
                                class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                            {{ item.label }}
                        </a>
                    </div>
                    <ResponsiveNavLink
                        v-else
                        v-if="!item.redirect && !item.profile"
                        :href="route(item.route, { backref: pathName })"
                        class="relative py-[20px] text-[16px] sm:text-[18px]"
                        data-drawer-hide="right-navigation">
                        <font-awesome-icon
                            :icon="['fas', item.icon]"
                            class="mx-auto mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                        {{ item.label }}
                        <div v-if="item.count" class="absolute right-0 top-[10px] leading-[40px]">
                            <span
                                class="block h-[21px] w-[21px] rounded-full bg-navred text-center text-[10px] font-medium leading-[21px]"
                                >{{ item.count }}</span
                            >
                        </div>
                        <div
                            v-if="item.coins"
                            class="absolute right-[15px] top-[21px] leading-[40px]">
                            <span
                                class="block h-[21px] w-auto rounded-full text-center text-[18px] font-medium leading-[21px] text-white">
                                ${{ getCointsValue(item.keyCount, profile) }}
                            </span>
                        </div>
                    </ResponsiveNavLink>
                    <ResponsiveNavLink
                        v-if="!item.redirect && item.profile"
                        :href="
                            route(item.route, [$page.props.auth.user.profile_url, item.view]) +
                            'user&backref=' +
                            pathName
                        "
                        class="relative py-[20px] text-[16px] sm:text-[18px]"
                        data-drawer-hide="right-navigation">
                        <font-awesome-icon
                            :icon="['fas', item.icon]"
                            class="mr-[14px] h-[20px] w-[20px] cursor-pointer text-gray-800 hover:text-gray-400 dark:text-white dark:hover:text-gray-400" />
                        {{ item.label }}
                    </ResponsiveNavLink>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
    .menuDiv,
    .insideMenu {
        overflow-y: scroll;
        -webkit-overflow-scrolling: touch; /* this is to enable momentum-based scrolling on iOS */
    }

    #insideMenu {
        transition: transform 0.1s ease-out;
    }
</style>
